//
// solar.ts — where the Sun and Moon are overhead
//
// Translation of `Utils/SolarEphemeris.swift`. Computes, with no network
// call and no API key:
//
//   - the sub-solar point (where the Sun is directly overhead),
//   - the sub-lunar point (same for the Moon),
//   - the day/night terminator (a 90° great circle around the sub-solar
//     point),
//   - trailing ground tracks for both.
//
// Why only the Sun and Moon: AZISUR plots points on Earth's *surface*, and
// only those two bodies have a meaningful sub-point. Mars and Jupiter have
// sky coordinates (RA/Dec) and no place on a ground map. Broader
// solar-system work would be a different visualisation entirely, fed by JPL
// Horizons.
//
// Accuracy, stated plainly:
//   Sun  — a few hundredths of a degree.
//   Moon — roughly 0.3° to 1.0°. A truncated lunar theory keeping only the
//          dominant periodic terms: enough to place a marker, not enough to
//          navigate by.
//

export interface Subpoint {
  latitude: number;
  longitude: number;
}

const deg2rad = (d: number) => (d * Math.PI) / 180;
const rad2deg = (r: number) => (r * 180) / Math.PI;

function normalizeLongitude(lon: number): number {
  let l = lon % 360;
  while (l > 180) l -= 360;
  while (l < -180) l += 360;
  return l;
}

/** Days since the J2000.0 epoch — the "n" both formulas below run on. */
function daysSinceJ2000(dateMs: number): number {
  return dateMs / 86400000 + 2440587.5 - 2451545.0;
}

/** Greenwich Mean Sidereal Time in degrees, normalised to [0, 360). */
function gmstDegrees(n: number): number {
  let hours = (18.697374558 + 24.06570982441908 * n) % 24;
  if (hours < 0) hours += 24;
  return hours * 15;
}

/**
 * The point where the Sun is directly overhead. Its latitude is the Sun's
 * declination; its longitude comes from the Greenwich hour angle. This is
 * also the centre of the illuminated hemisphere — the pole of the
 * terminator circle.
 */
export function subsolarPoint(dateMs: number = Date.now()): Subpoint {
  const n = daysSinceJ2000(dateMs);

  const meanLongitude = (280.46 + 0.9856474 * n) % 360;
  const meanAnomaly = deg2rad((357.528 + 0.9856003 * n) % 360);
  const eclipticLongitude = deg2rad(
    meanLongitude + 1.915 * Math.sin(meanAnomaly) + 0.02 * Math.sin(2 * meanAnomaly),
  );
  const obliquity = deg2rad(23.439 - 0.0000004 * n);

  const declination = Math.asin(Math.sin(obliquity) * Math.sin(eclipticLongitude));
  const rightAscension = Math.atan2(
    Math.cos(obliquity) * Math.sin(eclipticLongitude),
    Math.cos(eclipticLongitude),
  );

  return {
    latitude: rad2deg(declination),
    longitude: normalizeLongitude(gmstDegrees(n) - rad2deg(rightAscension)),
  };
}

/** The point directly beneath the Moon. See the accuracy note in the header. */
export function sublunarPoint(dateMs: number = Date.now()): Subpoint {
  const n = daysSinceJ2000(dateMs);
  const obliquity = deg2rad(23.439 - 0.0000004 * n);

  const meanLongitude = (218.316 + 13.176396 * n) % 360;
  const meanAnomaly = deg2rad((134.963 + 13.064993 * n) % 360);
  const argOfLatitude = deg2rad((93.272 + 13.22935 * n) % 360);

  const eclipticLongitude = deg2rad(
    (meanLongitude + 6.289 * Math.sin(meanAnomaly)) % 360,
  );
  const eclipticLatitude = deg2rad(5.128 * Math.sin(argOfLatitude));

  const sinDec =
    Math.sin(eclipticLatitude) * Math.cos(obliquity) +
    Math.cos(eclipticLatitude) * Math.sin(obliquity) * Math.sin(eclipticLongitude);
  const declination = Math.asin(sinDec);

  const y =
    Math.sin(eclipticLongitude) * Math.cos(obliquity) -
    Math.tan(eclipticLatitude) * Math.sin(obliquity);
  const rightAscension = Math.atan2(y, Math.cos(eclipticLongitude));

  return {
    latitude: rad2deg(declination),
    longitude: normalizeLongitude(gmstDegrees(n) - rad2deg(rightAscension)),
  };
}

/**
 * Points around the day/night terminator — the great circle 90° from the
 * sub-solar point. Returned in [lon, lat] order, ready for `tracePath`.
 */
export function terminatorRing(
  dateMs: number = Date.now(),
  pointCount = 180,
): Array<[number, number]> {
  const subsolar = subsolarPoint(dateMs);
  const poleLat = deg2rad(subsolar.latitude);
  const poleLon = deg2rad(subsolar.longitude);
  const angularDistance = deg2rad(90);

  const ring: Array<[number, number]> = [];
  for (let i = 0; i < pointCount; i++) {
    const bearing = deg2rad((i / pointCount) * 360);
    const lat = Math.asin(
      Math.sin(poleLat) * Math.cos(angularDistance) +
        Math.cos(poleLat) * Math.sin(angularDistance) * Math.cos(bearing),
    );
    const lon =
      poleLon +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(poleLat),
        Math.cos(angularDistance) - Math.sin(poleLat) * Math.sin(lat),
      );
    ring.push([normalizeLongitude(rad2deg(lon)), rad2deg(lat)]);
  }
  return ring;
}

/**
 * Samples a subpoint function backward over `hoursBack`, producing a
 * trailing ground track — the luminary equivalent of a satellite's
 * trajectory, and the same shape, so the renderer draws both one way.
 */
export function luminaryPath(
  fn: (ms: number) => Subpoint,
  dateMs: number = Date.now(),
  hoursBack = 6,
  pointCount = 60,
): Array<{ lat: number; lon: number }> {
  const stepMs = (hoursBack * 3600 * 1000) / pointCount;
  const points: Array<{ lat: number; lon: number }> = [];
  for (let i = 0; i <= pointCount; i++) {
    const sp = fn(dateMs - (pointCount - i) * stepMs);
    points.push({ lat: sp.latitude, lon: sp.longitude });
  }
  return points;
}

export const subsolarPath = (dateMs?: number) => luminaryPath(subsolarPoint, dateMs);
export const sublunarPath = (dateMs?: number) => luminaryPath(sublunarPoint, dateMs);
